import styled from 'styled-components';
import { Icon } from '../../../../components/common/icon/Icon';
import { useEffect } from 'react';
import { useState } from 'react';
import { useRef } from 'react';
import { ampli } from '../../../../ampli';
import { useAppSelector, useAppDispatch } from '../../../../hooks/store/useStore';
import { toggleCropTrendsAccordion } from '../../../../store/accordionSlice';

interface AccordionProps {
  children: React.ReactNode;
  header: React.ReactNode;
  onHeaderClick?: () => void;
  middleCategoryName: string;
}

export const Accordion = ({ children, header, onHeaderClick, middleCategoryName }: AccordionProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const dispatch = useAppDispatch();

  // Redux에서 아코디언 상태 가져오기
  const isActive = useAppSelector((state) => state.accordion.cropTrendsListPage[middleCategoryName] ?? true);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [children]);

  const handleHeaderClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onHeaderClick) {
      onHeaderClick();
    }
  };

  const handleToggle = () => {
    ampli.pvmClickCropAccordionToggle({
      type: isActive ? 'close' : 'open',
    });
    dispatch(
      toggleCropTrendsAccordion({
        middleCategoryCode: middleCategoryName,
        isOpen: !isActive,
      }),
    );
  };

  return (
    <AccordionContainer>
      <AccordionHeader as="div">
        <AccordionHeaderContent onClick={handleHeaderClick} as="button" type="button">
          {header}
        </AccordionHeaderContent>
        <AccordionExpandIcon
          as="button"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
        >
          <ExpandIconWrapper>
            <AccordionExpandText>{isActive ? '닫기' : '열기'}</AccordionExpandText>
          </ExpandIconWrapper>
        </AccordionExpandIcon>
      </AccordionHeader>
      <AccordionContent
        ref={contentRef}
        isActive={isActive}
        style={{
          height: isActive ? contentHeight : 0,
          overflow: 'hidden',
        }}
      >
        <AccordionContentInner>{children}</AccordionContentInner>
      </AccordionContent>
    </AccordionContainer>
  );
};

const AccordionContainer = styled.div`
  width: 100%;
  border-radius: 16px;
`;

const AccordionHeader = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  background: #f2f2f7;
  border: none;
  transition: background-color 0.2s ease;
  border-radius: 16px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: 50px;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: #e0e0eb;
  }
`;

const AccordionHeaderContent = styled.div`
  flex: 1;

  display: flex;
  padding: 16px;
  justify-content: space-between;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const AccordionExpandIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  cursor: pointer;
  transition: transform 0.2s ease;

  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  -webkit-tap-highlight-color: transparent;

  align-self: stretch;
`;

const ExpandIconWrapper = styled.div`
  pointer-events: none;
`;

const AccordionContent = styled.div<{ isActive: boolean }>`
  transition: height 0.1s ease-in-out;
  background: white;
`;

const AccordionContentInner = styled.div``;

const AccordionExpandText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */
`;
